import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import { ethers } from "ethers";
import { useWeb3Context } from "../context/Web3Context";
import { useWallet } from "@solana/wallet-adapter-react";
import { PublicKey, Transaction } from "@solana/web3.js";
import {
  getAssociatedTokenAddress,
  createTransferInstruction,
  createAssociatedTokenAccountInstruction,
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { addresses, supportedChainIds } from "../constants";
import { toast } from "react-toastify";
import { bridgeService } from "../lib/bridgeService";
import {
  getTokenBalanceWithFallback,
  executeWithFallback,
} from "../lib/rpcFallback";
import WalletConnect from "../components/WalletConnect";
import { Web3Button } from "../components/web3UI/Web3Button";
import NetworkSwitcher from "../components/NetworkSwitcher";
import { Link, Link as RouterLink } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./BridgeStyles.scss";

// DBN Token ABI (simplified - add the actual ABI methods you need)
const DBN_TOKEN_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
  "function name() view returns (string)",
];

const Bridge = () => {
  return <BridgeContent />;
};

const BridgeContent = () => {
  const [bridgeAmount, setBridgeAmount] = useState("");
  const [destinationAddress, setDestinationAddress] = useState("");
  const [bscTokenBalance, setBscTokenBalance] = useState("0");
  const [solanaTokenBalance, setSolanaTokenBalance] = useState("0");
  const [loading, setLoading] = useState(false);
  const [txHash, setTxHash] = useState("");
  const [bridgeStatus, setBridgeStatus] = useState("");
  const [refreshingBalance, setRefreshingBalance] = useState(false);
  const [bridgeDirection, setBridgeDirection] = useState("bsc-to-solana"); // 'bsc-to-solana' or 'solana-to-bsc'

  // BSC Web3 Context
  const { web3Provider, address, network, connect: connectBSC } = useWeb3Context();

  // Solana Wallet Context
  const {
    publicKey: solanaPublicKey,
    connected: solanaConnected,
    sendTransaction,
    connect,
    wallet,
  } = useWallet();

  // Helper function to validate and ensure wallet connection
  const ensureWalletConnection = async () => {
    console.log("Checking wallet connection...");
    console.log("Connected:", solanaConnected);
    console.log("Public Key:", solanaPublicKey?.toString());
    console.log("Send Transaction Available:", !!sendTransaction);
    console.log("Wallet:", wallet?.adapter?.name);

    if (!solanaConnected || !solanaPublicKey || !sendTransaction) {
      console.log("Wallet not properly connected, attempting to reconnect...");

      if (wallet && !solanaConnected) {
        try {
          await connect();
          // Wait a moment for connection to stabilize
          await new Promise((resolve) => setTimeout(resolve, 1000));
        } catch (error) {
          console.error("Failed to reconnect wallet:", error);
          throw new Error(
            "Failed to reconnect wallet. Please manually reconnect and try again."
          );
        }
      }

      // Re-check after connection attempt
      if (!solanaConnected || !solanaPublicKey || !sendTransaction) {
        throw new Error(
          "Wallet is not properly connected. Please reconnect your wallet and try again."
        );
      }
    }

    return true;
  };

  // Update destination address when wallet connects
  useEffect(() => {
    if (
      bridgeDirection === "bsc-to-solana" &&
      solanaConnected &&
      solanaPublicKey
    ) {
      setDestinationAddress(solanaPublicKey.toString());
    } else if (bridgeDirection === "solana-to-bsc" && address) {
      setDestinationAddress(address);
    }
  }, [solanaConnected, solanaPublicKey, address, bridgeDirection]);

  // Fetch BSC DBN token balance with retry logic
  useEffect(() => {
    const fetchBSCBalance = async (retryCount = 0) => {
      if (!address || !network) {
        setBscTokenBalance("0");
        return;
      }

      try {
        const chainId = network.chainId;
        console.log("🚀 ~ Bridge.js:67 ~ fetchBSCBalance ~ chainId:", chainId);
        if (!supportedChainIds.includes(chainId)) {
          setBscTokenBalance("0");
          return;
        }

        const tokenAddress = addresses[chainId]?.dbonAddress;
        if (!tokenAddress) {
          console.warn(`No DBN token address found for chain ID: ${chainId}`);
          setBscTokenBalance("0");
          return;
        }

        console.log(
          `Fetching DBN balance for ${address} on chain ${chainId}...`
        );

        // Use the new RPC fallback system
        const result = await getTokenBalanceWithFallback(
          tokenAddress,
          address,
          DBN_TOKEN_ABI,
          chainId
        );

        console.log("DBN Balance:", result.formatted);
        setBscTokenBalance(result.formatted);
      } catch (error) {
        console.error(
          "Error fetching BSC balance (attempt",
          retryCount + 1,
          "):",
          error.message
        );

        // Retry logic for persistent RPC errors
        if (retryCount < 2) {
          console.log("Retrying balance fetch in 2 seconds...");
          setTimeout(() => fetchBSCBalance(retryCount + 1), 2000);
          return;
        }

        // Set fallback balance after all retries
        setBscTokenBalance("0");

        // Show user-friendly error message
        if (retryCount >= 2) {
          toast.error(
            "Unable to fetch balance. Network issues detected. Please try refreshing."
          );
        }
      }
    };

    fetchBSCBalance();
  }, [address, network]);

  // Fetch Solana DBN token balance
  useEffect(() => {
    const fetchSolanaBalance = async () => {
      if (!solanaConnected || !solanaPublicKey) {
        setSolanaTokenBalance("0");
        return;
      }

      try {
        console.log(
          `Fetching Solana DBN balance for ${solanaPublicKey.toString()}...`
        );

        // Get the actual Solana DBN token balance
        const tokenMintAddress = process.env.REACT_APP_SPL_TOKEN_MINT_ADDRESS;
        if (!tokenMintAddress) {
          console.warn("REACT_APP_SPL_TOKEN_MINT_ADDRESS not configured");
          setSolanaTokenBalance("0");
          return;
        }

        // Use the bridge service to get Solana balance
        const balance = await bridgeService.getSolanaTokenBalance(
          solanaPublicKey.toString(),
          tokenMintAddress
        );

        console.log("Solana DBN Balance:", balance);
        setSolanaTokenBalance(balance);
      } catch (error) {
        console.error("Error fetching Solana balance:", error);
        setSolanaTokenBalance("0");
        toast.error("Unable to fetch Solana balance.");
      }
    };

    fetchSolanaBalance();
  }, [solanaConnected, solanaPublicKey]);

  // Debug wallet connection state changes
  useEffect(() => {
    console.log("🔗 Wallet State Changed:", {
      solanaConnected,
      hasPublicKey: !!solanaPublicKey,
      publicKeyString: solanaPublicKey?.toString(),
      hasSendTransaction: !!sendTransaction,
      walletName: wallet?.adapter?.name,
      bridgeDirection,
    });
  }, [
    solanaConnected,
    solanaPublicKey,
    sendTransaction,
    wallet,
    bridgeDirection,
  ]);

  // Manual balance refresh function
  const refreshBalance = async () => {
    setRefreshingBalance(true);

    try {
      if (bridgeDirection === "bsc-to-solana" && address && network) {
        const chainId = network.chainId;

        if (!supportedChainIds.includes(chainId)) return;

        const tokenAddress = addresses[chainId]?.dbonAddress;
        if (!tokenAddress) return;

        // Use the new RPC fallback system for refresh
        const result = await getTokenBalanceWithFallback(
          tokenAddress,
          address,
          DBN_TOKEN_ABI,
          chainId
        );

        setBscTokenBalance(result.formatted);
        toast.success("BSC balance refreshed successfully!");
      } else if (
        bridgeDirection === "solana-to-bsc" &&
        solanaConnected &&
        solanaPublicKey
      ) {
        // Refresh actual Solana balance
        console.log(
          `Refreshing Solana balance for ${solanaPublicKey.toString()}...`
        );

        const tokenMintAddress = process.env.REACT_APP_SPL_TOKEN_MINT_ADDRESS;
        if (!tokenMintAddress) {
          console.warn("REACT_APP_SPL_TOKEN_MINT_ADDRESS not configured");
          return;
        }

        // Use the bridge service to get Solana balance
        const balance = await bridgeService.getSolanaTokenBalance(
          solanaPublicKey.toString(),
          tokenMintAddress
        );

        setSolanaTokenBalance(balance);
        toast.success("Solana balance refreshed successfully!");
      }
    } catch (error) {
      console.error("Error refreshing balance:", error);
      toast.error("Failed to refresh balance. Network issues detected.");
    } finally {
      setRefreshingBalance(false);
    }
  };

  // Validate Solana address
  const isValidSolanaAddress = (addr) => {
    try {
      new PublicKey(addr);
      return true;
    } catch {
      return false;
    }
  };

  // Validate BSC address
  const isValidBSCAddress = (addr) => {
    return ethers.utils.isAddress(addr);
  };

  // Get validation function based on bridge direction
  const isValidDestinationAddress = (addr) => {
    if (bridgeDirection === "bsc-to-solana") {
      return isValidSolanaAddress(addr);
    } else {
      return isValidBSCAddress(addr);
    }
  };

  // Handle bridge transaction
  const handleBridge = async () => {
    if (bridgeDirection === "bsc-to-solana") {
      await handleBSCToSolana();
    } else {
      await handleSolanaToBSC();
    }
  };

  // Handle BSC to Solana bridge
  const handleBSCToSolana = async () => {
    if (!web3Provider || !address || !network) {
      toast.error("Please connect your BSC wallet");
      return;
    }

    if (!bridgeAmount || parseFloat(bridgeAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!destinationAddress || !isValidSolanaAddress(destinationAddress)) {
      toast.error("Please enter a valid Solana wallet address");
      return;
    }

    if (parseFloat(bridgeAmount) > parseFloat(bscTokenBalance)) {
      toast.error("Insufficient DBN token balance");
      return;
    }

    setLoading(true);
    setBridgeStatus("Initiating BSC to Solana bridge...");

    try {
      const chainId = network.chainId;
      const tokenAddress = addresses[chainId]?.dbonAddress;

      if (!tokenAddress) {
        throw new Error("DBN token address not found for this network");
      }

      // Get decimals using fallback RPC (for reading)
      setBridgeStatus("Getting token information...");
      const tokenInfo = await executeWithFallback(async (provider) => {
        const contract = new ethers.Contract(
          tokenAddress,
          DBN_TOKEN_ABI,
          provider
        );
        const decimals = await contract.decimals();
        return { decimals };
      }, chainId);

      // Use user's provider for the actual transaction
      const signer = web3Provider.getSigner();
      const contract = new ethers.Contract(tokenAddress, DBN_TOKEN_ABI, signer);

      // Send tokens to BSC admin wallet (users send tokens here)
      const bscAdminWallet =
        process.env.REACT_APP_BSC_ADMIN_WALLET_ADDRESS ||
        "0x000000000000000000000000000000000000dEaD";

      const amount = ethers.utils.parseUnits(bridgeAmount, tokenInfo.decimals);

      setBridgeStatus("Sending DBN tokens to bridge admin wallet...");

      // Send tokens to admin wallet
      const tx = await contract.transfer(bscAdminWallet, amount);
      setTxHash(tx.hash);

      setBridgeStatus("Waiting for transaction confirmation...");

      // Wait for transaction confirmation
      const receipt = await tx.wait();

      if (receipt.status === 1) {
        setBridgeStatus(
          "Transaction confirmed. Submitting bridge request for admin approval..."
        );

        // Call your backend API to process the bridge
        await processBridgeRequest({
          direction: "bsc-to-solana",
          bscTxHash: tx.hash,
          fromAddress: address,
          toSolanaAddress: destinationAddress,
          amount: bridgeAmount,
          chainId: chainId,
        });

        setBridgeStatus(
          "Bridge request submitted successfully! Admin will review and process your request."
        );
        toast.success(
          "Bridge request submitted for admin approval! You will receive DBN tokens on Solana after verification."
        );

        // Reset form
        setBridgeAmount("");
        setDestinationAddress("");
      } else {
        throw new Error("Transaction failed");
      }
    } catch (error) {
      console.error("Bridge error:", error);
      setBridgeStatus("Bridge failed");
      toast.error(`Bridge failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle Solana to BSC bridge
  const handleSolanaToBSC = async () => {
    setLoading(true);
    setBridgeStatus("Validating wallet connection...");

    try {
      // Ensure wallet is properly connected before proceeding
      await ensureWalletConnection();

      if (!bridgeAmount || parseFloat(bridgeAmount) <= 0) {
        throw new Error("Please enter a valid amount");
      }

      if (!destinationAddress || !isValidBSCAddress(destinationAddress)) {
        throw new Error("Please enter a valid BSC wallet address");
      }

      if (parseFloat(bridgeAmount) > parseFloat(solanaTokenBalance)) {
        throw new Error("Insufficient DBN token balance on Solana");
      }

      setBridgeStatus("Initiating Solana to BSC bridge...");

      const tokenMintAddress = process.env.REACT_APP_SPL_TOKEN_MINT_ADDRESS;
      const solanaAdminWallet =
        process.env.REACT_APP_SOLANA_ADMIN_WALLET_ADDRESS;

      if (!tokenMintAddress || !solanaAdminWallet) {
        throw new Error(
          "Solana configuration missing. Please check environment variables."
        );
      }

      setBridgeStatus("Validating token accounts...");

      // Create and send Solana token transfer transaction
      const tokenMintPublicKey = new PublicKey(tokenMintAddress);
      const adminWalletPublicKey = new PublicKey(solanaAdminWallet);

      console.log("Token mint:", tokenMintAddress);
      console.log("Admin wallet:", solanaAdminWallet);
      console.log("User wallet:", solanaPublicKey.toString());

      // Get associated token accounts
      const fromTokenAccount = await getAssociatedTokenAddress(
        tokenMintPublicKey,
        solanaPublicKey
      );

      const toTokenAccount = await getAssociatedTokenAddress(
        tokenMintPublicKey,
        adminWalletPublicKey
      );

      console.log("From token account:", fromTokenAccount.toString());
      console.log("To token account:", toTokenAccount.toString());

      // Check if the user's token account exists and has sufficient balance
      try {
        const fromAccountInfo =
          await bridgeService.solanaConnection.getAccountInfo(fromTokenAccount);
        if (!fromAccountInfo) {
          throw new Error(
            "Your token account does not exist. You need to receive DBN tokens first."
          );
        }
        console.log("From account exists:", !!fromAccountInfo);
      } catch (error) {
        console.error("Error checking from account:", error);
        throw new Error(
          "Failed to verify your token account. Please ensure you have DBN tokens."
        );
      }

      // Check if the admin token account exists and create if needed
      const instructions = [];
      try {
        const toAccountInfo =
          await bridgeService.solanaConnection.getAccountInfo(toTokenAccount);
        console.log("To account exists:", !!toAccountInfo);
        if (!toAccountInfo) {
          console.log(
            "Admin token account does not exist. Adding create account instruction."
          );
          // Add instruction to create the associated token account
          const createAccountInstruction =
            createAssociatedTokenAccountInstruction(
              solanaPublicKey, // payer
              toTokenAccount, // associated token account
              adminWalletPublicKey, // owner
              tokenMintPublicKey, // mint
              TOKEN_PROGRAM_ID,
              ASSOCIATED_TOKEN_PROGRAM_ID
            );
          instructions.push(createAccountInstruction);
        }
      } catch (error) {
        console.warn("Could not verify admin token account:", error);
        // Add create account instruction as a safety measure
        const createAccountInstruction =
          createAssociatedTokenAccountInstruction(
            solanaPublicKey, // payer
            toTokenAccount, // associated token account
            adminWalletPublicKey, // owner
            tokenMintPublicKey, // mint
            TOKEN_PROGRAM_ID,
            ASSOCIATED_TOKEN_PROGRAM_ID
          );
        instructions.push(createAccountInstruction);
      }

      setBridgeStatus("Creating transfer transaction...");

      // Convert amount to token units (assuming 9 decimals for DBN)
      const amountInTokenUnits = Math.floor(
        parseFloat(bridgeAmount) * Math.pow(10, 9)
      );
      console.log("Amount in token units:", amountInTokenUnits);

      // Validate amount
      if (amountInTokenUnits <= 0) {
        throw new Error("Invalid transfer amount");
      }

      // Create transfer instruction
      const transferInstruction = createTransferInstruction(
        fromTokenAccount,
        toTokenAccount,
        solanaPublicKey,
        amountInTokenUnits,
        [],
        TOKEN_PROGRAM_ID
      );
      instructions.push(transferInstruction);

      // Create transaction with all instructions
      const transaction = new Transaction().add(...instructions);

      setBridgeStatus("Preparing transaction for signing...");

      // Get recent blockhash
      const { blockhash } =
        await bridgeService.solanaConnection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = solanaPublicKey;

      console.log("Transaction prepared:");
      console.log("- Recent blockhash:", blockhash);
      console.log("- Fee payer:", solanaPublicKey.toString());
      console.log("- Instructions count:", transaction.instructions.length);

      // Final wallet connection validation just before sending
      await ensureWalletConnection();

      console.log("Sending transaction with wallet:", wallet?.adapter?.name);
      console.log("Transaction details:", {
        fromAccount: fromTokenAccount.toString(),
        toAccount: toTokenAccount.toString(),
        amount: amountInTokenUnits,
        feePayer: solanaPublicKey.toString(),
      });

      setBridgeStatus("Sending transaction to blockchain...");

      // Send transaction with additional options
      const txSignature = await sendTransaction(
        transaction,
        bridgeService.solanaConnection,
        {
          skipPreflight: false,
          preflightCommitment: "processed",
        }
      );

      setBridgeStatus("Waiting for transaction confirmation...");

      // Wait for confirmation
      await bridgeService.solanaConnection.confirmTransaction(
        txSignature,
        "confirmed"
      );

      setTxHash(txSignature);
      setBridgeStatus(
        "Transaction confirmed. Submitting bridge request for admin approval..."
      );

      // Call your backend API to process the bridge
      await processBridgeRequest({
        direction: "solana-to-bsc",
        solanaSignature: txSignature,
        fromSolanaAddress: solanaPublicKey.toString(),
        toBSCAddress: destinationAddress,
        amount: bridgeAmount,
      });

      setBridgeStatus(
        "Bridge request submitted successfully! Admin will review and process your request."
      );
      toast.success(
        "Bridge request submitted for admin approval! You will receive DBN tokens on BSC after verification."
      );

      // Reset form
      setBridgeAmount("");
      setDestinationAddress("");
    } catch (error) {
      console.error("Bridge error:", error);
      setBridgeStatus("Bridge failed");

      // Provide specific error messages based on error type
      let errorMessage = "Unknown error occurred";

      if (error.name === "WalletSendTransactionError") {
        if (error.message.includes("not connected")) {
          errorMessage =
            "Wallet is not connected. Please reconnect your wallet and try again.";
        } else if (error.message.includes("rejected")) {
          errorMessage =
            "Transaction was rejected by wallet. Please try again.";
        } else if (error.message.includes("Unexpected error")) {
          errorMessage =
            "Transaction failed unexpectedly. This might be due to insufficient SOL for fees, invalid token accounts, or network issues. Please check your SOL balance and try again.";
        } else {
          errorMessage = `Wallet transaction error: ${error.message}`;
        }
      } else if (error.message.includes("Wallet disconnected")) {
        errorMessage =
          "Wallet disconnected during transaction. Please reconnect and try again.";
      } else if (error.message.includes("token account does not exist")) {
        errorMessage =
          "Token account not found. Please ensure you have received DBN tokens first.";
      } else if (error.message.includes("Insufficient")) {
        errorMessage = "Insufficient balance or funds for transaction.";
      } else if (error.message.includes("Failed to verify")) {
        errorMessage =
          "Could not verify token account. Please check your connection and token balance.";
      } else if (error.message.includes("configuration missing")) {
        errorMessage =
          "Application configuration error. Please contact support.";
      } else if (error.message.includes("Invalid transfer amount")) {
        errorMessage =
          "Invalid amount specified. Please enter a valid positive number.";
      } else {
        errorMessage = error.message || "Transaction failed";
      }

      toast.error(`Bridge failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Process bridge request via backend API
  const processBridgeRequest = async (bridgeData) => {
    try {
      // Use the bridge service to submit the request for admin approval
      const result = await bridgeService.submitBridgeRequest(bridgeData);
      console.log("Bridge request submitted:", result);
      return result;
    } catch (error) {
      console.error("Error submitting bridge request:", error);
      throw error;
    }
  };

  return (
    <div className="dashboard-body">
      <div className="bridge-page">
        <div className="dashboard-head">
          <div className="logo">
            <Link to="/">
              <img
                className="main_logo"
                src={require("../images/logo.png")}
                style={{ width: "200px" }}
              />
            </Link>
          </div>
          <div className="buttons">
            <Link to="/">
              <button className="btn main-btn">Home</button>
            </Link>
            <Web3Button />
          </div>
        </div>
        <Container className="bridge-container mt-5 mb-5">
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <div className="bridge-card">
                <Card.Header className="text-center">
                  <h2 className="w-600">DBN Token Bridge</h2>
                  <p className="text-muted">
                    Bridge DBN tokens between BSC and Solana
                  </p>

                  {/* Bridge Direction Selector */}
                  <div className="bridge-direction-selector mb-3">
                    <Button
                      size="sm"
                      className={` ${
                        bridgeDirection === "bsc-to-solana" ? "main-btn" : ""
                      }`}
                      onClick={() => setBridgeDirection("bsc-to-solana")}
                    >
                      BSC → Solana
                    </Button>
                    <Button
                      size="sm"
                      className={` ${
                        bridgeDirection === "solana-to-bsc" ? "main-btn" : ""
                      }`}
                      onClick={() => setBridgeDirection("solana-to-bsc")}
                    >
                      Solana → BSC
                    </Button>
                  </div>

                  <div className="text-end">
                    <RouterLink
                      to="/bridge/history"
                      className="btn main-btn btn-sm"
                    >
                      <i className="fas fa-history me-1"></i>
                      View History
                    </RouterLink>
                  </div>
                </Card.Header>

                <Card.Body>
                  {/* Source Chain Section */}
                  <div className="bridge-section mb-4">
                    <h5 className="section-title">
                      {bridgeDirection === "bsc-to-solana" ? (
                        <>
                          <img
                            src={require("../images/BNB.png")}
                            alt="BSC"
                            className="chain-icon"
                          />
                          From: Binance Smart Chain
                        </>
                      ) : (
                        <>
                          <i
                            className="fas fa-rocket me-2"
                            style={{ color: "#00D4AA" }}
                          ></i>
                          From: Solana Network
                        </>
                      )}
                    </h5>

                    <div className="wallet-info mb-3">
                      <small className="text-muted">Connected Wallet:</small>
                      <div className="wallet-address">
                        {bridgeDirection === "bsc-to-solana"
                          ? address
                            ? `${address.slice(0, 6)}...${address.slice(-4)}`
                            : "Not connected"
                          : solanaPublicKey
                          ? `${solanaPublicKey.toString().slice(0, 8)}...`
                          : "Not connected"}
                      </div>
                      <div className="balance-info d-flex justify-content-between align-items-center">
                        <strong>
                          DBN Balance:{" "}
                          {bridgeDirection === "bsc-to-solana"
                            ? parseFloat(bscTokenBalance).toFixed(4)
                            : parseFloat(solanaTokenBalance).toFixed(4)}
                        </strong>
                        {((bridgeDirection === "bsc-to-solana" && address) ||
                          (bridgeDirection === "solana-to-bsc" &&
                            solanaPublicKey)) && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            onClick={refreshBalance}
                            disabled={refreshingBalance}
                            style={{ color: "#e348ff" }}
                          >
                            <i
                              className={`fas fa-${
                                refreshingBalance
                                  ? "spinner fa-spin"
                                  : "refresh"
                              }`}
                            ></i>
                          </Button>
                        )}
                      </div>

                      {/* Debug info for wallet connection - remove in production */}
                      {process.env.NODE_ENV === "development" &&
                        bridgeDirection === "solana-to-bsc" && (
                          <div className="feature-wrapper">
                            <small className="text-muted d-block">
                              Debug Info:
                            </small>
                            <ul className="list">
                              <li className="d-block">
                                Connected: {solanaConnected ? "✅" : "❌"} (
                                {String(solanaConnected)})
                              </li>
                              <li className="d-block">
                                PublicKey: {solanaPublicKey ? "✅" : "❌"} (
                                {solanaPublicKey ? "Present" : "null"})
                              </li>
                              <li className="d-block">
                                SendTransaction: {sendTransaction ? "✅" : "❌"}
                              </li>
                              <li className="d-block">
                                Wallet: {wallet?.adapter?.name || "None"}
                              </li>
                              <li className="d-block">
                                Show Alert:{" "}
                                {!solanaConnected || !solanaPublicKey
                                  ? "✅"
                                  : "❌"}
                              </li>
                            </ul>
                          </div>
                        )}
                    </div>

                    {bridgeDirection === "bsc-to-solana" && !address && (
                      <div className="">
                        <Button 
                          variant="primary" 
                          className='main-btn' 
                          onClick={connectBSC}
                        >
                          <i className="fas fa-link me-2"></i>
                          Connect BSC Wallet
                        </Button>
                      </div>
                    )}

                    {/* Network Switcher for unsupported networks */}
                    {bridgeDirection === "bsc-to-solana" && address && (
                      <NetworkSwitcher />
                    )}

                    {bridgeDirection === "solana-to-bsc" &&
                      (!solanaConnected || !solanaPublicKey) && (
                        <div className="mb-3">
                          <Alert className="select-wallet-alert-wrapper">
                            <span>
                              Please connect your Solana wallet to bridge from
                              Solana to BSC
                            </span>
                            <WalletConnect />
                          </Alert>
                        </div>
                      )}

                    <Form.Group className="mt-4">
                      <Form.Label>Amount to Bridge</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter DBN amount"
                        value={bridgeAmount}
                        onChange={(e) => setBridgeAmount(e.target.value)}
                        disabled={
                          loading ||
                          (bridgeDirection === "bsc-to-solana" && !address) ||
                          (bridgeDirection === "solana-to-bsc" &&
                            (!solanaConnected || !solanaPublicKey))
                        }
                        step="0.0001"
                        min="0"
                      />
                      <Button
                        variant="link"
                        size="sm"
                        className="max-button"
                        onClick={() =>
                          setBridgeAmount(
                            bridgeDirection === "bsc-to-solana"
                              ? bscTokenBalance
                              : solanaTokenBalance
                          )
                        }
                        disabled={
                          loading ||
                          (bridgeDirection === "bsc-to-solana" && !address) ||
                          (bridgeDirection === "solana-to-bsc" &&
                            (!solanaConnected || !solanaPublicKey))
                        }
                      >
                        Max
                      </Button>
                    </Form.Group>
                  </div>

                  {/* Arrow Icon */}
                  <div className="bridge-arrow text-center mb-4">
                    <i className="fas fa-arrow-down fa-2x text-primary"></i>
                  </div>

                  {/* Destination Chain Section */}
                  <div className="bridge-section mb-4">
                    <h5 className="section-title">
                      {bridgeDirection === "bsc-to-solana" ? (
                        <>
                          <i
                            className="fas fa-rocket me-2"
                            style={{ color: "#00D4AA" }}
                          ></i>
                          To: Solana Network
                        </>
                      ) : (
                        <>
                          <img
                            src={require("../images/BNB.png")}
                            alt="BSC"
                            className="chain-icon"
                          />
                          To: Binance Smart Chain
                        </>
                      )}
                    </h5>

                    <Form.Group className="">
                      <Form.Label>
                        {bridgeDirection === "bsc-to-solana"
                          ? "Solana Wallet Address"
                          : "BSC Wallet Address"}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={
                          bridgeDirection === "bsc-to-solana"
                            ? "Enter Solana wallet address"
                            : "Enter BSC wallet address"
                        }
                        value={destinationAddress}
                        onChange={(e) => setDestinationAddress(e.target.value)}
                        disabled={loading}
                      />
                      <Form.Text className="text-muted d-block mt-3">
                        {bridgeDirection === "bsc-to-solana"
                          ? "Enter the Solana address where you want to receive DBN tokens"
                          : "Enter the BSC address where you want to receive DBN tokens"}
                      </Form.Text>
                    </Form.Group>

                    {destinationAddress &&
                      !isValidDestinationAddress(destinationAddress) && (
                        <Alert variant="warning" className="mb-0 mt-3">
                          Invalid{" "}
                          {bridgeDirection === "bsc-to-solana"
                            ? "Solana"
                            : "BSC"}{" "}
                          address format
                        </Alert>
                      )}
                  </div>

                  {/* Bridge Status */}
                  {bridgeStatus && (
                    <Alert
                      variant={
                        bridgeStatus.includes("failed") ? "danger" : "info"
                      }
                      className="mb-3"
                    >
                      {bridgeStatus}
                    </Alert>
                  )}

                  {/* Transaction Hash */}
                  {txHash && (
                    <Alert variant="success" className="mb-3">
                      <small>
                        Transaction Hash:
                        <a
                          href={
                            bridgeDirection === "bsc-to-solana"
                              ? `https://bscscan.com/tx/${txHash}`
                              : `https://solscan.io/tx/${txHash}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ms-2"
                        >
                          {txHash.slice(0, 10)}...{txHash.slice(-8)}
                        </a>
                      </small>
                    </Alert>
                  )}

                  {/* Bridge Button */}
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-100 bridge-button"
                    onClick={handleBridge}
                    disabled={
                      loading ||
                      !bridgeAmount ||
                      !destinationAddress ||
                      !isValidDestinationAddress(destinationAddress) ||
                      parseFloat(bridgeAmount) <= 0 ||
                      (bridgeDirection === "bsc-to-solana" &&
                        (!address ||
                          parseFloat(bridgeAmount) >
                            parseFloat(bscTokenBalance))) ||
                      (bridgeDirection === "solana-to-bsc" &&
                        (!solanaConnected ||
                          parseFloat(bridgeAmount) >
                            parseFloat(solanaTokenBalance)))
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Processing...
                      </>
                    ) : (
                      `Bridge DBN Tokens (${
                        bridgeDirection === "bsc-to-solana"
                          ? "BSC → Solana"
                          : "Solana → BSC"
                      })`
                    )}
                  </Button>

                  {/* Bridge Info */}
                  <div className="bridge-info mt-4">
                    <Alert variant="info">
                      <h6>Bridge Wallet Addresses:</h6>
                      {bridgeDirection === "bsc-to-solana" ? (
                        <div>
                          <p className="mb-2">
                            <strong>Send your BSC DBN tokens to:</strong>
                          </p>
                          <div className="code-wrapper p-2 rounded d-block">
                            {process.env.REACT_APP_BSC_ADMIN_WALLET_ADDRESS ||
                              "0x000000000000000000000000000000000000dEaD"}
                          </div>
                          <small className="text-muted d-block mt-3">
                            You will receive DBN tokens on Solana after admin
                            approval
                          </small>
                        </div>
                      ) : (
                        <div>
                          <p className="mb-2">
                            <strong>Send your Solana DBN tokens to:</strong>
                          </p>
                          <code className="code-wrapper p-2 rounded d-block">
                            {process.env
                              .REACT_APP_SOLANA_ADMIN_WALLET_ADDRESS ||
                              "11111111111111111111111111111112"}
                          </code>
                          <small className="text-muted d-block mt-3">
                            You will receive DBN tokens on BSC after admin
                            approval
                          </small>
                        </div>
                      )}
                    </Alert>

                    <Alert variant="secondary">
                      <h6>How it works:</h6>
                      {bridgeDirection === "bsc-to-solana" ? (
                        <ol className="mb-0">
                          <li>Connect your BSC wallet and enter the amount</li>
                          <li>Enter your Solana wallet address</li>
                          <li>
                            Confirm the transaction to send DBN tokens to admin
                            wallet
                          </li>
                          <li>
                            Admin will manually review and send tokens to your
                            Solana address (usually within 10-30 minutes)
                          </li>
                        </ol>
                      ) : (
                        <ol className="mb-0">
                          <li>
                            Connect your Solana wallet and enter the amount
                          </li>
                          <li>Enter your BSC wallet address</li>
                          <li>
                            Confirm the transaction to send DBN tokens to admin
                            wallet
                          </li>
                          <li>
                            Admin will manually review and send tokens to your
                            BSC address (usually within 10-30 minutes)
                          </li>
                        </ol>
                      )}
                    </Alert>
                  </div>
                </Card.Body>
              </div>
            </Col>
          </Row>
        </Container>
        <div className="footer_section">
          <div className="section_container">
            <img
              src={require("../images/logo.png")}
              style={{ width: "250px" }}
            />
            <p>
              We make it easy to Discover, Invest and manage all your NFTs at
              one place.
            </p>
            <div className="icons">
              <a href="https://twitter.com/DashBone1" target="_blank">
                <i className="fa-brands fa-twitter"></i>
              </a>
              <a href="https://discord.gg/KVpw6gjzPf" target="_blank">
                <i className="fa-brands fa-discord"></i>
              </a>
              <a
                href="https://www.instagram.com/dashboneofficial/"
                target="_blank"
              >
                <i className="fa-brands fa-instagram"></i>
              </a>

              <a
                href="https://www.linkedin.com/in/dash-bone-11b37b268/"
                target="_blank"
              >
                <i className="fa-brands fa-linkedin-in"></i>
              </a>
              <a href="https://medium.com/@dashbone" target="_blank">
                <i className="fa-brands fa-medium"></i>
              </a>
              <a
                href="https://www.pinterest.com.au/dashboneio/"
                target="_blank"
              >
                <i className="fa-brands fa-pinterest-p"></i>
              </a>
              <a
                href="https://www.quora.com/profile/Dash-Bone-2"
                target="_blank"
              >
                <i className="fa-brands fa-quora"></i>
              </a>
            </div>
            <img
              className="anim_pic14"
              src={require("../images/circle-01.png")}
              style={{ width: "16px" }}
            />
            <img
              className="anim_pic15"
              src={require("../images/ethereum-01.png")}
              style={{ width: "32px" }}
            />
            <img
              className="anim_pic16"
              src={require("../images/x-01.png")}
              style={{ width: "16px" }}
            />
          </div>
        </div>
        <div className="footer_bottom">
          <div className="section_container">
            <span>© 2023 Dashbone. All rights reserved.</span>
            {/* <span>© 2022 Dashbone. All rights reserved.</span> */}
          </div>
          <img
            className="anim_pic17"
            src={require("../images/circle-03.png")}
            style={{ width: "24px" }}
          />
        </div>
      </div>
    </div>
  );
};

export default Bridge;
